import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { UserPlanI } from '../../api/users/getPlans';
import { findArcaPlanById } from '../../constants/arcaPlans';
import { ViewType } from '../../types/dashboard';
import { isNewPlanTransfer } from '../../types/individualDashboard';
import {
	PENDING_PERSONAL_OPTION_KEY,
	isLivePlan,
	isPersonalViewPlan,
	viewTypeOfPlan
} from '../viewOptions';
import { orderedSelectablePlans } from '../../components/Navigation/planSelectorItems';
import {
	ESTADOS_VIVOS,
	TipoAccionPendiente,
	accionPendienteDelTraspaso,
	estadoDeIdentidad
} from '../traspasos/tarjetasDeTraspaso';

export type TipoPlanResumen = 'EMPLEO' | 'INDIVIDUAL' | 'AUTONOMO';
export type EstadoPlanResumen = 'activo' | 'suspendido' | 'enProceso';

/** Lo que pinta una PlanCard del Resumen (Fase A1: sin rentabilidad ni aportación todavía). */
export interface ResumenPlanVM {
	/** El `user_epp_config.id`, o `PENDING_PERSONAL_OPTION_KEY` para el plan en proceso. */
	id: string;
	tipo: TipoPlanResumen;
	estado: EstadoPlanResumen;
	nombre: string | null;
	/** Solo empleo: se subtitula con la empresa. */
	empresa: string | null;
	gestora: string | null;
	/** `null` = «sin dato», nunca 0 €. */
	saldoEuros: number | null;
	/** Lo que valía el 1 de enero, para la rentabilidad del año. `null` = «sin dato», nunca 0 €. */
	saldoInicioAnioEuros: number | null;
	/** El día de esa foto (`AAAA-MM-DD`): lo que entra y sale se cuenta desde el siguiente. */
	saldoInicioAnioFecha: string | null;
	/**
	 * El traspaso vivo más reciente de este plan, uno por card. Sin ninguno vivo, el completado que
	 * aún no ha visto (hasta que entra a su plan).
	 */
	traspaso: PensionPlanTransfer | null;
	/**
	 * Qué espera ese traspaso de la persona, para que su card lo diga (`ui/PlanCard` en Proa:
	 * «Pendiente» de firma o de destino). Nunca en empleo: ahí no se pide nada de esto.
	 */
	pendienteTraspaso: TipoAccionPendiente | null;
	/** Plan personal con la identidad sin verificar: se ve, pero no se opera. */
	soloLectura: boolean;
}

export interface EntradaPlanesResumen {
	plans: UserPlanI[];
	pendingPersonalPlan: boolean;
	/** El plan que eligió en el funnel, para nombrar el plan en proceso. */
	pendingPlanArcaId: string | null | undefined;
	kycStatus: string | null | undefined;
	/** `/kyc/status` respondió. Sin respuesta no se afirma solo lectura (ni lo contrario). */
	kycResuelto: boolean;
	transfers: PensionPlanTransfer[];
	/** Boletines ya firmados: un traspaso firmado ya no espera la firma. */
	signedBulletinIds: string[];
	/**
	 * Si la card de ese plan aún enseña este traspaso completado (`planVisto.completadoPorVer`).
	 * Sin ella, ningún completado se enseña.
	 */
	completadoPorVer?: (transfer: PensionPlanTransfer, planId: string) => boolean;
}

const tipoDe = (plan: UserPlanI): TipoPlanResumen => {
	if (plan.type === 'FREELANCE') return 'AUTONOMO';
	return viewTypeOfPlan(plan) === ViewType.USER ? 'EMPLEO' : 'INDIVIDUAL';
};

const masReciente = (transfers: PensionPlanTransfer[]): PensionPlanTransfer | null =>
	[...transfers].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;

/**
 * Las PlanCards del Resumen (product#6, §3.3), en el orden del selector de planes (empleo primero)
 * y con el plan personal en proceso al final.
 *
 * - «Suspendido» solo en EMPLEO: un individual suspendido sigue siendo Individual (Notion 08/09).
 * - `vestedRightsCents: null` es «sin dato», nunca 0 €.
 * - Un traspaso enlazado (`userEppConfigId`, que comparsa rellena al ejecutar el alta o aceptar la
 *   STDE) va a su plan. Uno sin enlazar va al plan personal cuyo catálogo es el plan de Arca
 *   elegido; si no se sabe, al único plan personal, solo si no hay empleo vivo (podría ser un
 *   traspaso al empleo); en otro caso a ninguno (no se adivina); sin plan personal todavía, a la
 *   card del plan en proceso.
 */
export function buildResumenPlans({
	plans,
	pendingPersonalPlan,
	pendingPlanArcaId,
	kycStatus,
	kycResuelto,
	transfers,
	signedBulletinIds,
	completadoPorVer = () => false
}: EntradaPlanesResumen): ResumenPlanVM[] {
	const vivos = orderedSelectablePlans(plans) as UserPlanI[];
	const personales = vivos.filter(isPersonalViewPlan);
	const identidadPendiente = kycResuelto && estadoDeIdentidad(kycStatus) !== null;
	const hayEmpleoVivo = vivos.some((plan) => isLivePlan(plan) && !isPersonalViewPlan(plan));
	const traspasosVivos = transfers.filter((t) => ESTADOS_VIVOS.includes(t.status ?? ''));

	const destinoPersonal = (t: PensionPlanTransfer): string | null => {
		const slug = t.selectedArcaPlanId ? findArcaPlanById(t.selectedArcaPlanId)?.catalogSlug : null;
		const porCatalogo = slug ? personales.find((p) => p.planCatalogSlug === slug) : undefined;
		if (porCatalogo) return porCatalogo.id;
		// Con un plan de empleo vivo no se adivina: un traspaso pedido desde Tu beneficio llega sin
		// `userEppConfigId` ni destino hasta que comparsa lo enlaza al empleo.
		if (personales.length === 1 && !hayEmpleoVivo) return personales[0].id;
		if (personales.length === 0 && pendingPersonalPlan) return PENDING_PERSONAL_OPTION_KEY;
		return null;
	};
	const planDelTraspaso = (t: PensionPlanTransfer): string | null =>
		t.userEppConfigId ?? destinoPersonal(t);
	const traspasoVivoDe = (id: string) =>
		masReciente(traspasosVivos.filter((t) => planDelTraspaso(t) === id));
	const completadoDe = (id: string) =>
		masReciente(
			transfers.filter(
				(t) =>
					t.status === 'completed' &&
					// El alta de un plan nuevo no es un traspaso: no trae dinero que «se haya recibido».
					t.kind !== 'NEW_PLAN' &&
					!isNewPlanTransfer(t) &&
					planDelTraspaso(t) === id &&
					completadoPorVer(t, id)
			)
		);
	const pendienteDe = (tipo: TipoPlanResumen, traspaso: PensionPlanTransfer | null) =>
		tipo === 'EMPLEO'
			? null
			: accionPendienteDelTraspaso({
					traspasoVivo: traspaso,
					kycStatus,
					kycResuelto,
					signedBulletinIds
				})?.tipo ?? null;

	const cards: ResumenPlanVM[] = vivos.filter(isLivePlan).map((plan) => {
		const tipo = tipoDe(plan);
		const vivo = traspasoVivoDe(plan.id);
		return {
			id: plan.id,
			tipo,
			estado: tipo === 'EMPLEO' && plan.status === 'suspended' ? 'suspendido' : 'activo',
			nombre: plan.planDisplayName,
			empresa: tipo === 'EMPLEO' ? plan.companyName : null,
			gestora: plan.managerName,
			saldoEuros: plan.vestedRightsCents === null ? null : plan.vestedRightsCents / 100,
			saldoInicioAnioEuros:
				plan.vestedRightsAtYearStartCents == null ? null : plan.vestedRightsAtYearStartCents / 100,
			saldoInicioAnioFecha: plan.vestedRightsAtYearStartDate ?? null,
			traspaso: vivo ?? completadoDe(plan.id),
			// Solo un traspaso vivo espera algo: el completado no pide nada (ni identidad).
			pendienteTraspaso: pendienteDe(tipo, vivo),
			soloLectura: tipo !== 'EMPLEO' && identidadPendiente
		};
	});

	if (!pendingPersonalPlan) return cards;
	const elegido = pendingPlanArcaId ? findArcaPlanById(pendingPlanArcaId) : undefined;
	const traspasoEnProceso = traspasoVivoDe(PENDING_PERSONAL_OPTION_KEY);
	return [
		...cards,
		{
			id: PENDING_PERSONAL_OPTION_KEY,
			tipo: 'INDIVIDUAL',
			estado: 'enProceso',
			nombre: elegido?.name ?? null,
			empresa: null,
			gestora: elegido?.manager ?? null,
			saldoEuros: null,
			saldoInicioAnioEuros: null,
			saldoInicioAnioFecha: null,
			traspaso: traspasoEnProceso,
			pendienteTraspaso: pendienteDe('INDIVIDUAL', traspasoEnProceso),
			soloLectura: identidadPendiente
		}
	];
}
